import { CartUpdateEvent } from '@theme/events';
import '@theme/fly-to-cart';

const ADD_ALL_SELECTOR = '.add-all-btn[data-variants]';
const LOADING_TEXT = 'Adding...';
const ADDED_TEXT = 'Added';
const ERROR_TEXT = 'Try again';
const RESET_DELAY = 1800;
const DEFAULT_FLY_IMAGE =
  'https://cdn.shopify.com/s/files/1/0729/5097/2504/files/Sandstone_Song.png?v=1780033699';
const FLY_IMAGE_SIZE = '56px';

function injectStyles() {
  if (document.getElementById('add-all-to-cart-styles')) return;

  const style = document.createElement('style');
  style.id = 'add-all-to-cart-styles';
  style.textContent = `
    .add-all-btn {
      position: relative;
      overflow: hidden;
      transition: transform 0.18s ease, opacity 0.18s ease, background-color 0.18s ease;
      -webkit-tap-highlight-color: transparent;
    }

    .add-all-btn.is-loading {
      cursor: wait;
      opacity: 0.75;
      pointer-events: none;
    }

    .add-all-btn.is-loading::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent, rgb(255 255 255 / 35%), transparent);
      transform: translateX(-100%);
      animation: add-all-button-shimmer 0.9s ease-in-out infinite;
    }

    .add-all-btn.is-added {
      animation: add-all-button-success 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) both;
    }

    .add-all-btn.is-error {
      animation: add-all-button-error 0.32s ease both;
    }

    @keyframes add-all-button-shimmer {
      to {
        transform: translateX(100%);
      }
    }

    @keyframes add-all-button-success {
      0% {
        transform: scale(1);
      }

      45% {
        transform: scale(0.96);
      }

      100% {
        transform: scale(1);
      }
    }

    @keyframes add-all-button-error {
      0%,
      100% {
        transform: translateX(0);
      }

      25% {
        transform: translateX(-3px);
      }

      75% {
        transform: translateX(3px);
      }
    }
  `;

  document.head.appendChild(style);
}

function getRoutes() {
  return {
    cartAddUrl: window.Theme?.routes?.cart_add_url || '/cart/add.js',
    cartUrl: '/cart.js',
  };
}

function getVariantIds(button) {
  return (button.dataset.variants || '')
    .split(',')
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isFinite(value) && value > 0);
}

function getButtonLabel(button) {
  return button.querySelector('[data-add-all-label], span');
}

function getButtonText(button) {
  const label = getButtonLabel(button);

  return (label || button).textContent?.trim() || 'Add';
}

function setButtonText(button, text) {
  const label = getButtonLabel(button);

  if (label) {
    label.textContent = text;
    return;
  }

  button.textContent = text;
}

function setButtonState(button, state, text) {
  button.classList.toggle('is-loading', state === 'loading');
  button.classList.toggle('is-added', state === 'added');
  button.classList.toggle('is-error', state === 'error');
  button.dataset.addAllState = state;
  button.disabled = state === 'loading';

  if (text) {
    setButtonText(button, text);
  }
}

function resetButton(button) {
  const originalText = button.dataset.addAllOriginalText || 'Add';

  delete button.dataset.addAllState;
  button.classList.remove('is-loading', 'is-added', 'is-error');
  button.disabled = false;
  setButtonText(button, originalText);
}

function getFlyImage(button) {
  if (button.dataset.flyImage) return button.dataset.flyImage;
  if (DEFAULT_FLY_IMAGE) return DEFAULT_FLY_IMAGE;

  const container = button.closest(
    '[data-product-card], product-card, .product-card, slideshow-slide, .slideshow-slide, .card, section'
  );
  const image = container?.querySelector('img.product-img, .product-media img, .card-gallery img, img');

  if (!(image instanceof HTMLImageElement)) return '';

  return image.currentSrc || image.src || '';
}

function animateFlyToCart(button) {
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  if (!cartIcon || !customElements.get('fly-to-cart')) return;

  const flyToCartElement = document.createElement('fly-to-cart');
  const image = getFlyImage(button);

  flyToCartElement.classList.add('fly-to-cart--quick');
  flyToCartElement.style.setProperty('--start-opacity', '0');
  flyToCartElement.style.setProperty('--width', FLY_IMAGE_SIZE);
  flyToCartElement.style.setProperty('--height', FLY_IMAGE_SIZE);
  flyToCartElement.style.setProperty('background-size', 'cover');
  flyToCartElement.style.setProperty('background-position', 'center');

  if (image) {
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
  }

  flyToCartElement.source = button;
  flyToCartElement.destination = cartIcon;

  document.body.appendChild(flyToCartElement);
}

async function addVariantsToCart(button, variantIds) {
  const { cartAddUrl, cartUrl } = getRoutes();
  const response = await fetch(cartAddUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: variantIds.map((id) => ({
        id,
        quantity: 1,
      })),
    }),
  });
  const result = await response.json().catch(() => ({}));

  if (!response.ok || result.status) {
    throw new Error(result.description || result.message || 'Unable to add products');
  }

  const cartResponse = await fetch(`${cartUrl}?_t=${Date.now()}`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!cartResponse.ok) {
    throw new Error('Unable to refresh cart');
  }

  const cart = await cartResponse.json();
  document.dispatchEvent(
    new CartUpdateEvent(cart, button.id || 'add-all-button', {
      source: 'add-all-button',
      itemCount: Number(cart.item_count) || 0,
    })
  );

  return cart;
}

async function handleAddAllClick(event) {
  const button = event.target instanceof Element ? event.target.closest(ADD_ALL_SELECTOR) : null;
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  if (button.dataset.addAllState === 'loading') return;

  const variantIds = getVariantIds(button);
  if (!variantIds.length) return;

  if (!button.dataset.addAllOriginalText) {
    button.dataset.addAllOriginalText = getButtonText(button);
  }

  setButtonState(button, 'loading', LOADING_TEXT);

  try {
    await addVariantsToCart(button, variantIds);
    animateFlyToCart(button);
    setButtonState(button, 'added', button.dataset.addedText || ADDED_TEXT);
  } catch (error) {
    console.error(error);
    setButtonState(button, 'error', button.dataset.errorText || ERROR_TEXT);
  } finally {
    window.setTimeout(() => resetButton(button), RESET_DELAY);
  }
}

injectStyles();
document.addEventListener('click', handleAddAllClick, { capture: true });
